import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { graphql } from '@api/generated/gql';
import { TransferDestinationType } from '@api/generated/graphql';
import useService from '@modules/ServiceModule/useService';
import ServiceModule from '@modules/ServiceModule';
import { convertTimeSpanToSeconds } from '@lib/utils/time';
import { QueueRow } from '../QueuesTable/types';

const SHORT_ROLLOVER_TIMEOUT_SECONDS = 30 * 60;

const getQueuesForTransferQuery = graphql(`
  query getQueuesForTransfer {
    queuesForTransfer {
      id
      name
      entrypoints
      metadata {
        transferRollover {
          destinationType
          workflowId
          workflowExtensions
        }
      }
    }
  }
`);

/**
 * Provides queue data for the transfer modal: queue list and dial targets from
 * `queuesForTransfer`, with `availableUsers`, `estimatedWait`, and the rollover
 * fallback warning derived from RTCS service state (same source as
 * `useServiceQueuesState`).
 *
 * `hasFallbackWarning` is true when the queue has no usable rollover
 * destination (transfer rollover metadata is missing, the destinationType is
 * unset, or destinationType is `EndCall`) AND the rollover timeout is shorter
 * than 30 minutes — mirroring the warning shown in the queue rollover settings
 * form.
 *
 * When `useStrictAvailability` is true, `availableUsers` counts only agents
 * who can actually be offered a queue call (Available status AND `None` or
 * `Call-Offer` activity), matching the call center monitoring "Capacity"
 * widget. When false, it falls back to the legacy "set to Available"
 * definition.
 */
export const useTransferQueuesState = ({
  useStrictAvailability = false,
  computeFallbackWarning = false,
}: {
  useStrictAvailability?: boolean;
  computeFallbackWarning?: boolean;
} = {}) => {
  const { data, loading: gqlLoading } = useQuery(getQueuesForTransferQuery);
  const {
    queueById,
    userByContactCenterUserId,
    loading: serviceLoading,
  } = useService();

  const queues: QueueRow[] = useMemo(
    () =>
      (data?.queuesForTransfer ?? []).map((queue) => {
        const svc = queueById[queue.id];
        const isAvailable = useStrictAvailability
          ? ServiceModule.isUserAvailableForCalls
          : ServiceModule.isUserAvailable;
        const availableUsers = svc
          ? svc.memberIds.filter((memberId) => {
              const user = userByContactCenterUserId[memberId];
              return user ? isAvailable(user) : false;
            }).length
          : 0;

        const destinationType =
          queue.metadata?.transferRollover?.destinationType;
        const hasNoFallbackDestination =
          !destinationType ||
          destinationType === TransferDestinationType.EndCall;
        const rolloverTimeoutSeconds = convertTimeSpanToSeconds(
          svc?.rolloverSettings?.timeout ?? undefined,
        );
        const hasFallbackWarning =
          computeFallbackWarning &&
          hasNoFallbackDestination &&
          rolloverTimeoutSeconds > 0 &&
          rolloverTimeoutSeconds < SHORT_ROLLOVER_TIMEOUT_SECONDS;

        return {
          id: queue.id,
          name: queue.name,
          number:
            queue.metadata?.transferRollover?.workflowExtensions?.[0] ??
            queue.entrypoints[0] ??
            '',
          availableUsers,
          estimatedWait: svc?.status?.estimatedWait,
          hasFallbackWarning,
        };
      }),
    [
      data,
      queueById,
      userByContactCenterUserId,
      useStrictAvailability,
      computeFallbackWarning,
    ],
  );

  return { queues, loading: gqlLoading || serviceLoading };
};
